import React from 'react';
import { Easing, interpolate, useVideoConfig } from 'remotion';
import { Caption, VideoTheme, Word as WordType } from '../../types';
import { EmojiDisplay } from './EmojiDisplay';
import { Word } from './Word';

interface AliProps {
  caption: Caption;
  currentFrame: number;
  theme: VideoTheme;
  fontSize: number;
  captionPosY: number;
  highlightColors: string[];
  gap: number;
  emojiAnimationData: any;
}

export const Ali: React.FC<AliProps> = ({
  caption,
  currentFrame,
  theme,
  fontSize,
  captionPosY,
  highlightColors,
  gap,
  emojiAnimationData,
}) => {
  const { fps } = useVideoConfig();
  const { emoji, words } = caption;

  return (
    <div
      style={{
        ...styles.container,
        bottom: `${captionPosY}px`,
        columnGap: `${gap}px`,
        background: theme.captionStyle.highlightColor1,
      }}
    >
      <EmojiDisplay emoji={emoji} theme={theme} emojiAnimationData={emojiAnimationData} />
      <WordList
        words={words}
        currentFrame={currentFrame}
        fps={fps}
        highlightColors={highlightColors}
        theme={theme}
        fontSize={fontSize}
      />
    </div>
  );
};

// --------------------------------------------------------------------------------

interface WordListProps {
  words: WordType[];
  currentFrame: number;
  fps: number;
  highlightColors: string[];
  theme: VideoTheme;
  fontSize: number;
}

const WordList: React.FC<WordListProps> = ({
  words,
  currentFrame,
  fps,
  highlightColors,
  theme,
  fontSize,
}) => {
  return (
    <>
      {words.map((wordObj, index) => {
        const { word, startTime, highlightColorId, lineBreak } = wordObj;

        const startFrame = startTime * fps;

        const highLightColor = highlightColorId ? highlightColors[Number(highlightColorId)] : '';

        const relativeFrame = currentFrame - startFrame;

        const fadeIn = interpolate(relativeFrame, [0, 3], [0.5, 1], {
          extrapolateRight: 'clamp',
          extrapolateLeft: 'clamp',
          easing: Easing.ease,
        });

        return (
          <React.Fragment key={index}>
            {lineBreak && <div style={{ flex: '100%' }} />}
            <Word
              word={word}
              color={highLightColor || theme.fontStyle.fontColor}
              fontSize={fontSize}
              theme={theme}
              customWordStyle={{
                WebkitFontSmoothing: 'antialiased',
                opacity: fadeIn,
                textTransform: 'none',
              }}
            />
          </React.Fragment>
        );
      })}
    </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    top: undefined,
    left: '50%',
    lineHeight: '1',
    background: '#e7e5e7',
    transform: 'translateX(-50%)',
    borderRadius: '8px',
    boxSizing: 'border-box',
    padding: '.46em .8em .43em',
    rowGap: 0,
    maxWidth: '80%',
  },
};
